import React from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import { formatDateTimeCompact } from "@/utils/dateUtils";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useExportJob } from "@/hooks/useDataExport";
import {
  ExportJob,
  ExportJobStatus,
  ExportProgressStep,
} from "@/types/data-export";
import { ExportStatusBadge } from "./ExportStatusBadge";

interface ExportJobStatusViewProps {
  jobId: string;
  onClose: () => void;
}

// Canonical backend `progress[].step` identifiers → i18n label keys. Unknown
// steps fall back to the raw backend string.
const STEP_LABEL_KEY: Record<string, string> = {
  "region:gather": "regionGather",
  "catalog:gather": "catalogGather",
  "archive:upload": "archiveUpload",
};

const ProgressRow: React.FC<{ step: ExportProgressStep }> = ({ step }) => {
  const { t } = useTranslation();
  const labelKey = STEP_LABEL_KEY[step.step];
  const label = labelKey ? t(`dataExport.job.steps.${labelKey}`) : step.step;

  return (
    <li className="flex items-start gap-2 py-1.5 text-sm">
      {step.status === "failed" ? (
        <XCircle className="mt-0.5 size-4 shrink-0 text-custom-destructive-dark" />
      ) : (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-light-green" />
      )}
      <div className="flex flex-col">
        <span className="text-foreground">{label}</span>
        {typeof step.count === "number" ? (
          <span className="text-xs text-muted-foreground">{step.count}</span>
        ) : null}
        {step.error ? (
          <span className="text-xs text-custom-destructive-dark">
            {step.error}
          </span>
        ) : null}
      </div>
    </li>
  );
};

const JobMeta: React.FC<{ job: ExportJob }> = ({ job }) => {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataExport.job.meta.requestedBy")}
        </dt>
        <dd className="break-all">{job.requestedByEmail ?? "—"}</dd>
      </div>
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataExport.job.meta.started")}
        </dt>
        <dd>{formatDateTimeCompact(job.startedAt)}</dd>
      </div>
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataExport.job.meta.completed")}
        </dt>
        <dd>{formatDateTimeCompact(job.completedAt)}</dd>
      </div>
    </dl>
  );
};

const JobProgress: React.FC<{ steps: ExportProgressStep[] | null }> = ({
  steps,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-foreground">
        {t("dataExport.job.progressHeading")}
      </h4>
      {steps && steps.length > 0 ? (
        <ul className="divide-y rounded-lg border px-3">
          {steps.map((step, index) => (
            <ProgressRow key={`${step.step}-${index}`} step={step} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("dataExport.job.noProgress")}
        </p>
      )}
    </div>
  );
};

const JobCounts: React.FC<{ counts: Record<string, number> }> = ({
  counts,
}) => {
  const { t } = useTranslation();
  const entries = Object.entries(counts);
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-foreground">
        {t("dataExport.job.summaryHeading")}
      </h4>
      {entries.length > 0 ? (
        <ul className="rounded-lg border">
          {entries.map(([entity, count]) => (
            <li
              key={entity}
              className="flex items-center justify-between gap-3 border-b px-3 py-1.5 text-sm last:border-b-0"
            >
              <span className="font-mono text-muted-foreground">{entity}</span>
              <span className="font-medium">{count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("dataExport.job.noCounts")}
        </p>
      )}
    </div>
  );
};

const JobWarnings: React.FC<{ warnings: string[] }> = ({ warnings }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-light-orange/50 bg-light-orange/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-light-orange">
        <TriangleAlert className="size-4" />
        <h4 className="text-sm font-semibold">
          {t("dataExport.job.warningsHeading")}
        </h4>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        {t("dataExport.job.warningsLead")}
      </p>
      <ul className="list-disc pl-5 text-sm text-foreground">
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
    </div>
  );
};

const JobDownload: React.FC<{ job: ExportJob }> = ({ job }) => {
  const { t } = useTranslation();
  const url = job.downloadUrl;

  if (!url) {
    return (
      <div className="rounded-lg border border-light-orange/50 bg-light-orange/5 p-4 text-sm text-foreground">
        {t("dataExport.job.expired")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        variant="new_default"
        className="w-fit"
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      >
        <Download className="size-4" />
        {t("dataExport.job.download")}
      </Button>
      {job.expiresAt ? (
        <span className="text-xs text-muted-foreground">
          {t("dataExport.job.availableUntil", {
            date: formatDateTimeCompact(job.expiresAt),
          })}
        </span>
      ) : null}
    </div>
  );
};

const JobError: React.FC<{ error: string | null }> = ({ error }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-4">
      <h4 className="mb-1 text-sm font-semibold text-custom-destructive-dark">
        {t("dataExport.job.errorHeading")}
      </h4>
      <p className="mb-2 text-xs text-muted-foreground">
        {t("dataExport.job.errorLead")}
      </p>
      {error ? (
        <p className="break-words text-sm text-custom-destructive-dark">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const JobBody: React.FC<{ job: ExportJob }> = ({ job }) => {
  const warnings = job.summary?.warnings ?? [];
  const isCompleted = job.status === ExportJobStatus.COMPLETED;
  return (
    <>
      <JobMeta job={job} />
      {isCompleted ? <JobDownload job={job} /> : null}
      <JobProgress steps={job.progress} />
      {isCompleted ? <JobCounts counts={job.summary?.counts ?? {}} /> : null}
      {warnings.length > 0 ? <JobWarnings warnings={warnings} /> : null}
      {job.status === ExportJobStatus.FAILED ? (
        <JobError error={job.error} />
      ) : null}
    </>
  );
};

export const ExportJobStatusView: React.FC<ExportJobStatusViewProps> = ({
  jobId,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    data: job,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useExportJob(jobId);

  const title = job?.targetLabel ?? job?.targetId ?? t("dataExport.job.title");

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <DialogTitle className="break-all">{title}</DialogTitle>
          {job ? <ExportStatusBadge status={job.status} /> : null}
          {job ? (
            <Badge variant="new_secondary">
              {t(`dataExport.targetType.${job.targetType}`)}
            </Badge>
          ) : null}
        </div>
      </DialogHeader>

      {isLoading && !job ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("dataExport.job.loading")}
        </div>
      ) : null}

      {isError && !job ? (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {t("dataExport.job.loadError")}
        </div>
      ) : null}

      {job ? <JobBody job={job} /> : null}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={isFetching ? "size-4 animate-spin" : "size-4"}
          />
          {t("dataExport.job.refresh")}
        </Button>
        <Button type="button" variant="new_outline" onClick={onClose}>
          {t("dataExport.job.close")}
        </Button>
      </DialogFooter>
    </div>
  );
};
